import {
  Prisma,
  RootFiCompany,
  RootFiIntegration as RootFiIntegrationPlatform,
  RootfiCompanyUseCase,
} from '@prisma/client';

enum ISalesTaxInvoiceCategory {
  GOOD = 'GOOD',
  BAD = 'BAD',
}

export enum RootfiIntegrationCategory {
  ACCOUNTING = 'ACCOUNTING',
  ECOMMERCE = 'ECOMMERCE',
  PAYMENTS = 'PAYMENTS',
  CRM = 'CRM',
}

export enum RootfiSyncFrequency {
  HOURLY = 'HOURLY',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

/** RootFi Core v4 data models. Kept in sync with `enum RootfiDataModelV4` in `packages/prisma/schema.prisma`. */
export enum RootfiDataModelV4 {
  ACCOUNT = 'ACCOUNT',
  BALANCE_SHEET = 'BALANCE_SHEET',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  BANK_TRANSACTION = 'BANK_TRANSACTION',
  BILL = 'BILL',
  BILL_CREDIT_NOTE = 'BILL_CREDIT_NOTE',
  BILL_PAYMENT = 'BILL_PAYMENT',
  CASH_FLOW_STATEMENT = 'CASH_FLOW_STATEMENT',
  COMPANY_INFO = 'COMPANY_INFO',
  CONTACT = 'CONTACT',
  CURRENCY = 'CURRENCY',
  DOCUMENT = 'DOCUMENT',
  EXPENSE = 'EXPENSE',
  GOODS_RECEIPT_NOTE = 'GOODS_RECEIPT_NOTE',
  INCOME_STATEMENT = 'INCOME_STATEMENT',
  INVOICE = 'INVOICE',
  INVOICE_CREDIT_NOTE = 'INVOICE_CREDIT_NOTE',
  INVOICE_PAYMENT = 'INVOICE_PAYMENT',
  ITEM = 'ITEM',
  JOURNAL_ENTRY = 'JOURNAL_ENTRY',
  JOURNAL_LINE = 'JOURNAL_LINE',
  PROJECT = 'PROJECT',
  PURCHASE_ORDER = 'PURCHASE_ORDER',
  SALES_ORDER = 'SALES_ORDER',
  ESTIMATE = 'ESTIMATE',
  TAX_RATE = 'TAX_RATE',
  TRACKING_CATEGORY = 'TRACKING_CATEGORY',
  CASH_SALE = 'CASH_SALE',
  CASH_REFUND = 'CASH_REFUND',
  BALANCE = 'BALANCE',
  DISPUTE = 'DISPUTE',
  ORDER = 'ORDER',
  PAYOUT = 'PAYOUT',
  SUBSCRIPTION = 'SUBSCRIPTION',
  TRANSACTION = 'TRANSACTION',
  EVENT = 'EVENT',
  FULFILLMENT = 'FULFILLMENT',
  INVENTORY = 'INVENTORY',
  ADDRESS = 'ADDRESS',
  EXTERNAL_LINK = 'EXTERNAL_LINK',
  LINE_ITEM = 'LINE_ITEM',
  PHONE_NUMBER = 'PHONE_NUMBER',
  VARIANT = 'VARIANT',
}

export enum RootfiSyncDatamodels {
  BILLS = 'BILLS',
  BILL_CREDIT_NOTES = 'BILL_CREDIT_NOTES',
  INVOICES = 'INVOICES',
  CONTACTS = 'CONTACTS',
  PRODUCTS = 'PRODUCTS',
  CASH_REFUNDS = 'CASH_REFUNDS',
  INVOICE_CREDIT_NOTES = 'INVOICE_CREDIT_NOTES',
  SALES_ORDERS = 'SALES_ORDERS',
  TRACKING_CATEGORIES = 'TRACKING_CATEGORIES',
  JOURNAL_ENTRIES = 'JOURNAL_ENTRIES',
  CURRENCIES = 'CURRENCIES',

  BALANCE_SHEET = 'BALANCE_SHEET',
  PROFIT_AND_LOSS = 'PROFIT_AND_LOSS',
  CASH_FLOW_STATEMENT = 'CASH_FLOW_STATEMENT',

  ECOMMERCE_TRANSACTIONS = 'ECOMMERCE_TRANSACTIONS',
  ECOMMERCE_PRODUCTS = 'ECOMMERCE_PRODUCTS',
  ECOMMERCE_CUSTOMERS = 'ECOMMERCE_CUSTOMERS',
  ECOMMERCE_ORDERS = 'ECOMMERCE_ORDERS',

  PAYMENT_INVOICES = 'PAYMENT_INVOICES',
  PAYMENT_CUSTOMERS = 'PAYMENT_CUSTOMERS',
  PAYMENT_PRODUCTS = 'PAYMENT_PRODUCTS',
  PAYMENT_CREDIT_NOTES = 'PAYMENT_CREDIT_NOTES',

  ACCOUNTS = 'ACCOUNTS',
  INCOME_STATEMENT = 'INCOME_STATEMENT',
  COMPANY_INFO = 'COMPANY_INFO',
  CASH_SALES = 'CASH_SALES',
}

export const RootfiDataModelsPrefix = {
  [RootfiIntegrationCategory.ECOMMERCE]: 'ECOMMERCE_',
  [RootfiIntegrationCategory.PAYMENTS]: 'PAYMENT_',
  [RootfiIntegrationCategory.CRM]: 'CRM_',
};

export type RootfiSyncConfig = {
  integrationCategory: RootfiIntegrationCategory;
  dataModel: RootfiSyncDatamodels;
  isEnabled: boolean;
  syncFrequency: RootfiSyncFrequency;
  syncInterval: number;
  permissions: {
    CanRead: boolean;
    CanCreate: boolean;
    CanUpdate: boolean;
    CanDelete: boolean;
  };
  syncFromDate?: string;
};
export type RootFiSyncQuery = {
  corporationId?: number;
  rootFiCompanyId?: number;
};

export type JobSearchType = {
  jobs: { isJobCompleted: boolean; id: string }[];
};

export type InvoicePaymentsResponse = {
  invoice_payments: {
    contact: {
      data: {
        addresses: {
          data: {
            city: string;
            country: string;
            state: string;
            pincode: string;
            type: 'SHIPPING' | 'BILLING';
          }[];
        };
        name: string;
      };
    };
    invoice: {
      data: {
        currency: {
          data: {
            code: string;
          };
        };
        total_amount: number;
        tax_amount: number;
        sub_total: number;
      };
    };
    payment_date: string;
  }[];
};

export type InvoicesResponse = {
  invoices: {
    data: {
      amount_due: number;
      currency_rate: number;
      document_number: string;
      due_date: string;
      memo: string;
      status: string;
      tax_amount: number;
      total_amount: number;
      total_discount: number;
      posted_date: string;
      sub_total: number;
      contact: {
        data: {
          name: string;
        };
      };
      line_items: {
        data: {
          description: string;
          line_item_type: string;
          quantity: number;
          sub_total: number;
          tax_amount: number;
          total_amount: number;
          unit_amount: number;
          total_discount: number;
          tax: {
            code: string;
            name: string;
            tax_type: string;
            total_tax_rate: number;
          };
        }[];
      };
    }[];
  };
};

export type TInvoiceItem = Prisma.RootFiInvoiceGetPayload<{
  include: {
    contact: {
      include: {
        addresses: true;
      };
    };
    addresses: true;
    line_items: true;
  };
}> & {
  category: ISalesTaxInvoiceCategory;
  corporation: { id: number; corporationName: string } | null;
};

export type InvoicesList = TInvoiceItem[];

export type RootfiIntegrationScopeCategory =
  | 'READ'
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE';


// Derived integration stage exposed by the /external/rootfi/connection-state
// endpoint. Computed from RootFiConnectionStatus + RootfiSyncStatus by the
// backend's mapToIntegrationStage(). Consumed by the SYS_ROOTFI_SYNC runtime
// handler and the end-user task UI.
export enum RootfiIntegrationStage {
  NOT_STARTED = 'NotStarted',
  IN_PROGRESS = 'InProgress',
  FAILED = 'Failed',
  COMPLETED = 'Completed',
}

// Derived "is the connection currently usable" flag. ACTIVE iff RootFiCompany
// row has connectionStatus=CONNECTED AND syncEnabled=true. The runtime auto-
// complete predicate gates on this.
export enum RootfiConnectionActiveStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

/** `GET rootfi/company?corporationId=` list row — RootFi Core v4 scope shape (CompareSyncConfigScopeRow). */
export type RootfiCompanyListScopeRowV4 = {
  data_model: RootfiDataModelV4;
  category: RootfiIntegrationCategory;
  scope_granted: RootfiIntegrationScopeCategory[];
  scope_missing: RootfiIntegrationScopeCategory[];
};

// --- Default sync config: `GET rootfi/sync-config/default` (RootFi Core v4) ---

export type RootFiSyncConfigScopeAccessV4 = {
  READ: boolean;
  CREATE: boolean;
  UPDATE: boolean;
  DELETE: boolean;
};

export enum RootFiWorkspaceSyncFrequencyV4 {
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
  DAILY = 'DAILY',
  HOURLY = 'HOURLY',
  NEVER = 'NEVER',
}

export type RootFiSyncConfigItemV4 = {
  interval?: number;
  frequency: RootFiWorkspaceSyncFrequencyV4;
  dataModel: RootfiDataModelV4;
  category: RootfiIntegrationCategory;
  scopeAccess: RootFiSyncConfigScopeAccessV4;
  enabled: boolean;
  syncFrom?: string | null;
};

export type RootFiSyncConfigV4 = RootFiSyncConfigItemV4[];

export type RootFiDefaultSyncConfigV4Response = {
  defaultSyncConfig: Partial<
    Record<RootFiIntegrationPlatform, RootFiSyncConfigV4>
  >;
};

export type CreateRootFiInviteLinkSyncConfigItemV4 = {
  interval?: number;
  frequency: RootFiWorkspaceSyncFrequencyV4;
  data_model: RootfiDataModelV4;
  category: RootfiIntegrationCategory;
  scope_access: RootFiSyncConfigScopeAccessV4;
  enabled: boolean;
  sync_from?: string | null;
};

/** Invite-link snapshot nested on company list rows (RootFi Core v4). */
export type GetRootFiInviteLinkApiResponse = {
  id: number;
  inviteLinkId: string;
  companyId: number | null;
  integration: RootFiIntegrationPlatform;
  integrationCategory: RootfiIntegrationCategory;
  syncFrom: string | null;
  createdAt: string;
  dataModelConfigs?: RootFiSyncConfigItemV4[] | null;
};

export type RootfiCompanyListItemV4 = RootFiCompany & {
  scopeData?: RootfiCompanyListScopeRowV4[];
  inviteLink?: GetRootFiInviteLinkApiResponse | null;
  corporation?: {
    id: number;
    companyLegalName: string | null;
    incorporationCountry: string | null;
  } | null;
};

/** RootFi integration row from the company list API. */
export type RootfiIntegrationListItem = RootfiCompanyListItemV4;

export type ListRootFiCompaniesResponseV4 = {
  companies: RootfiCompanyListItemV4[];
};

/** Product suites a full-suite connection serves; mirrors LB `ROOTFI_FULL_SUITE_USE_CASES`. */
export const ROOTFI_FULL_SUITE_USE_CASES = [
  RootfiCompanyUseCase.SALES_TAX,
  RootfiCompanyUseCase.BOOKKEEPING,
  RootfiCompanyUseCase.OPERATIONAL_TRANSFER_PRICING,
];

/** Request body for Commenda `POST rootfi/invite-link`. */
export type CreateRootFiInviteLinkBody = {
  corporationId: number;
  useCases?: RootfiCompanyUseCase[];
  integration: RootFiIntegrationPlatform;
  companyName: string;
  syncFrom?: string;
  syncConfig?: CreateRootFiInviteLinkSyncConfigItemV4[];
  /** Workflow context: binds the created RootFiCompany to this SYS_ROOTFI_SYNC task. */
  serviceRequestTaskId?: number;
};

/** Response from Commenda `POST rootfi/invite-link` (RootFi invite-link detail). */
export type CreateRootFiInviteLinkResponse = {
  inviteLink: {
    id: number;
    inviteLinkId: string;
    companyId: number;
  };
};

/** Request body for Commenda `PATCH rootfi/invite-link/:inviteLinkUuid`. */
export type PatchRootFiInviteLinkBody = {
  companyName?: string;
  syncFrom?: string | null;
  syncConfig?: CreateRootFiInviteLinkSyncConfigItemV4[] | null;
  useCases?: RootfiCompanyUseCase[];
};

/** Response from Commenda `PATCH rootfi/invite-link/:inviteLinkUuid`. */
export type PatchRootFiInviteLinkResponse = {
  inviteLink: {
    id: number;
    inviteLinkId: string;
    companyId: number | null;
    integration: RootFiIntegrationPlatform;
    integrationCategory: RootfiIntegrationCategory;
    syncFrom: string | null;
    createdAt: string;
  };
};

export enum IntegrationType {
  // Accounting
  ZOHO_BOOKS = 'ZOHO_BOOKS',
  QUICKBOOKS = 'QUICKBOOKS',
  QUICKBOOKS_SANDBOX = 'QUICKBOOKS_SANDBOX',
  XERO = 'XERO',
  TALLY = 'TALLY',
  SAGE_CLOUD_ACCOUNTING = 'SAGE_CLOUD_ACCOUNTING',
  SAGE_ZA_CLOUD_ACCOUNTING = 'SAGE_ZA_CLOUD_ACCOUNTING',
  SAGE_INTACCT = 'SAGE_INTACCT',
  MS_DYNAMICS_365 = 'MS_DYNAMICS_365',
  MS_DYNAMICS_365_FO = 'MS_DYNAMICS_365_FO',
  EPICOR_PROPHET_21 = 'EPICOR_PROPHET_21',
  MYOB_BUSINESS = 'MYOB_BUSINESS',
  WAVE = 'WAVE',
  ODOO_ACCOUNTING = 'ODOO_ACCOUNTING',
  NETSUITE = 'NETSUITE',
  WAFEQ = 'WAFEQ',
  MEKARI_JURNAL = 'MEKARI_JURNAL',
  BUSY = 'BUSY',
  ROOTFI_SANDBOX = 'ROOTFI_SANDBOX',
  QOYOD = 'QOYOD',
  FRESHBOOKS = 'FRESHBOOKS',
  CLEARBOOKS = 'CLEARBOOKS',
  FREEAGENT = 'FREEAGENT',
  KASHFLOW = 'KASHFLOW',
  RILLET = 'RILLET',
  RILLET_SANDBOX = 'RILLET_SANDBOX',
  CAMPFIRE = 'CAMPFIRE',

  // Ecommerce
  AMAZON = 'AMAZON',
  FLIPKART = 'FLIPKART',
  SHOPIFY = 'SHOPIFY',
  BIG_COMMERCE = 'BIG_COMMERCE',
  WOO_COMMERCE = 'WOO_COMMERCE',
  WIX = 'WIX',
  SQUARESPACE = 'SQUARESPACE',
  GOOGLE_PLAYSTORE = 'GOOGLE_PLAYSTORE',

  // Payments
  ZOHO_INVOICE = 'ZOHO_INVOICE',
  STRIPE = 'STRIPE',
  STRIPE_TEST = 'STRIPE_TEST',
  STRIPE_SANDBOX = 'STRIPE_SANDBOX',
  PAYPAL = 'PAYPAL',
  PAYPAL_SANDBOX = 'PAYPAL_SANDBOX',
  RAZORPAY = 'RAZORPAY',
  SQUARE = 'SQUARE',
  SQUARE_SANDBOX = 'SQUARE_SANDBOX',
  CHARGEBEE = 'CHARGEBEE',
  MOLLIE = 'MOLLIE',
  PADDLE = 'PADDLE',
  PADDLE_SANDBOX = 'PADDLE_SANDBOX',
  PUZZLE = 'PUZZLE',
  PUZZLE_SANDBOX = 'PUZZLE_SANDBOX',

  // CRM
  ZOHO_CRM = 'ZOHO_CRM',
  HUBSPOT = 'HUBSPOT',
  PIPEDRIVE = 'PIPEDRIVE',
  SALES_FORCE = 'SALES_FORCE',
}

// Single source of truth: every IntegrationType maps to exactly one category.
// Adding a new value to the IntegrationType enum without listing it here is a
// COMPILE ERROR — TS rejects an incomplete `Record<IntegrationType, ...>`.
//
// Promoted from the section comments on the IntegrationType enum above so the
// categorisation is machine-readable, not just a visual grouping.
export const INTEGRATION_CATEGORY: Record<IntegrationType, RootfiIntegrationCategory> = {
  // Accounting
  [IntegrationType.ZOHO_BOOKS]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.QUICKBOOKS]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.QUICKBOOKS_SANDBOX]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.XERO]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.TALLY]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.SAGE_CLOUD_ACCOUNTING]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.SAGE_ZA_CLOUD_ACCOUNTING]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.SAGE_INTACCT]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.MS_DYNAMICS_365]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.MS_DYNAMICS_365_FO]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.EPICOR_PROPHET_21]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.MYOB_BUSINESS]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.WAVE]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.ODOO_ACCOUNTING]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.NETSUITE]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.WAFEQ]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.MEKARI_JURNAL]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.BUSY]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.ROOTFI_SANDBOX]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.QOYOD]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.FRESHBOOKS]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.CLEARBOOKS]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.FREEAGENT]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.KASHFLOW]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.RILLET]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.RILLET_SANDBOX]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.CAMPFIRE]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.PUZZLE]: RootfiIntegrationCategory.ACCOUNTING,
  [IntegrationType.PUZZLE_SANDBOX]: RootfiIntegrationCategory.ACCOUNTING,
  // Ecommerce
  [IntegrationType.AMAZON]: RootfiIntegrationCategory.ECOMMERCE,
  [IntegrationType.FLIPKART]: RootfiIntegrationCategory.ECOMMERCE,
  [IntegrationType.SHOPIFY]: RootfiIntegrationCategory.ECOMMERCE,
  [IntegrationType.BIG_COMMERCE]: RootfiIntegrationCategory.ECOMMERCE,
  [IntegrationType.WOO_COMMERCE]: RootfiIntegrationCategory.ECOMMERCE,
  [IntegrationType.WIX]: RootfiIntegrationCategory.ECOMMERCE,
  [IntegrationType.SQUARESPACE]: RootfiIntegrationCategory.ECOMMERCE,
  [IntegrationType.GOOGLE_PLAYSTORE]: RootfiIntegrationCategory.ECOMMERCE,
  // Payments
  [IntegrationType.ZOHO_INVOICE]: RootfiIntegrationCategory.PAYMENTS,
  [IntegrationType.STRIPE]: RootfiIntegrationCategory.PAYMENTS,
  [IntegrationType.STRIPE_TEST]: RootfiIntegrationCategory.PAYMENTS,
  [IntegrationType.STRIPE_SANDBOX]: RootfiIntegrationCategory.PAYMENTS,
  [IntegrationType.PAYPAL]: RootfiIntegrationCategory.PAYMENTS,
  [IntegrationType.PAYPAL_SANDBOX]: RootfiIntegrationCategory.PAYMENTS,
  [IntegrationType.RAZORPAY]: RootfiIntegrationCategory.PAYMENTS,
  [IntegrationType.SQUARE]: RootfiIntegrationCategory.PAYMENTS,
  [IntegrationType.SQUARE_SANDBOX]: RootfiIntegrationCategory.PAYMENTS,
  [IntegrationType.CHARGEBEE]: RootfiIntegrationCategory.PAYMENTS,
  [IntegrationType.MOLLIE]: RootfiIntegrationCategory.PAYMENTS,
  [IntegrationType.PADDLE]: RootfiIntegrationCategory.PAYMENTS,
  [IntegrationType.PADDLE_SANDBOX]: RootfiIntegrationCategory.PAYMENTS,
  // CRM
  [IntegrationType.ZOHO_CRM]: RootfiIntegrationCategory.CRM,
  [IntegrationType.HUBSPOT]: RootfiIntegrationCategory.CRM,
  [IntegrationType.PIPEDRIVE]: RootfiIntegrationCategory.CRM,
  [IntegrationType.SALES_FORCE]: RootfiIntegrationCategory.CRM,
};

// Inverted view: all integrations grouped by category. Derived from
// INTEGRATION_CATEGORY at module load — never hand-maintained.
export const INTEGRATIONS_BY_CATEGORY: Record<RootfiIntegrationCategory, IntegrationType[]> = (() => {
  const buckets: Record<RootfiIntegrationCategory, IntegrationType[]> = {
    [RootfiIntegrationCategory.ACCOUNTING]: [],
    [RootfiIntegrationCategory.ECOMMERCE]: [],
    [RootfiIntegrationCategory.PAYMENTS]: [],
    [RootfiIntegrationCategory.CRM]: [],
  };
  for (const [it, cat] of Object.entries(INTEGRATION_CATEGORY) as [IntegrationType, RootfiIntegrationCategory][]) {
    buckets[cat].push(it);
  }
  return buckets;
})();

// Categories each Rootfi use-case's invite link covers. Drives the workflow
// editor's integration dropdown filtering and downstream consumers that need
// "which integrations does this use-case support."
//
// Backend has its own copy at commenda-logical-backend/.../utils.ts:21
// (`getIntegrationCategories(useCase)`). The two repos can't share a package,
// so any change here must also land in the backend file.
export const USE_CASE_TO_CATEGORIES: Record<RootfiCompanyUseCase, RootfiIntegrationCategory[]> = {
  SALES_TAX: [
    RootfiIntegrationCategory.ACCOUNTING,
    RootfiIntegrationCategory.ECOMMERCE,
    RootfiIntegrationCategory.PAYMENTS,
  ],
  OPERATIONAL_TRANSFER_PRICING: [RootfiIntegrationCategory.ACCOUNTING],
  BOOKKEEPING: [RootfiIntegrationCategory.ACCOUNTING],
  // No use-case currently maps to CRM. CRM integrations live in
  // INTEGRATION_CATEGORY as headroom — when a CRM-tagged use-case is added,
  // listing CRM here exposes those integrations to the dropdown.
};
